import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { AssetType, MapCategoryType } from '@/models/guest-app/asset'
import { Controller, useForm } from 'react-hook-form'
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/atoms/modal'
import { Button } from '@/components/atoms/button'
import { InputField } from '@/components/molecules/input-field'
import { InputImage } from '@/components/molecules/settings/input-image'
import { updateAssetMapCategory } from '@/apis/aipass'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { AssetDetailContext } from '@/components/pages/setting/guest-app/asset-detail'

type Props = {
  asset: AssetType
  detail: MapCategoryType
  onCancel: () => void
  onSave: () => void
}

export type AssetFreeDetailFormValue = {
  title: string
  imagePath: string
  description: string
  rawDescription: string
}

export const MapCategoryDetailModal: React.FC<Props> = ({ asset, detail, onCancel, onSave: parentSave }) => {
  const { t } = useTranslation()
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<AssetFreeDetailFormValue>({
    defaultValues: {
      title: detail?.title || '',
      imagePath: detail?.imagePath || '',
    },
  })
  const { doReload, setIsLoading } = useContext(AssetDetailContext)
  const [isShowCropModal, setIsShowCropModal] = useState<boolean>(false)
  const { errorHandler } = useErrorHandler()

  const onSave = async (value: AssetFreeDetailFormValue) => {
    try {
      setIsLoading(true)
      await updateAssetMapCategory(asset.id, detail.id, value)
      doReload()
      parentSave()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Modal customCss={modalStyle} style={{ visibility: isShowCropModal ? 'hidden' : 'visible' }}>
        <ModalHeader>{t('GuestAppSetting.CreateCategory')}</ModalHeader>
        <ModalBody>
          <div css={modalBodyStyle}>
            <div css={inputTitleTextStyle}>
              {t('Title')}
              <div css={requireLabelTextStyle}>※</div>
            </div>
            <Controller
              control={control}
              rules={{ required: t('Required field has not been entered') }}
              name="title"
              render={({ field: { onChange, value } }) => <InputField value={value} handleChangeData={onChange} disabled />}
            />
            <Controller
              name="imagePath"
              control={control}
              rules={{ required: t('Please select {{item}}', { item: t('Image') }) }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <InputImage
                  customStyle={inputAreaStyle}
                  value={value}
                  onChange={onChange}
                  onShowModal={setIsShowCropModal}
                  aspect={16 / 9}
                  error={error?.message}
                />
              )}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button buttonType={3} height="38px" width="110px" marginRight={16} onClick={() => onCancel()}>
            {t('Cancel')}
          </Button>
          <Button buttonType={isValid ? 1 : 5} height="38px" width="110px" onClick={handleSubmit(onSave)}>
            {t('Save')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

const modalStyle = {
  height: 544,
  width: 600,
  minWidth: 600,
  left: 'calc((100% - 600px) / 2)',
}

const modalBodyStyle = {
  padding: 32,
  border: '1px solid #ddd',
  borderRadius: 8,
  backgroundColor: '#fff',
  height: '100%',
  display: 'flex',
  flexFlow: 'column',
}

const inputTitleTextStyle = css({
  display: 'flex',
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: '0.6px',
  color: '#676767',
  paddingBottom: 12,
})

const requireLabelTextStyle = css({
  fontSize: 8,
  fontWeight: 'bold',
  letterSpacing: '0.4px',
  color: '#676767',
  paddingLeft: 8,
})

const inputAreaStyle = css({
  paddingTop: 24,
  display: 'flex',
})
